/**
 * Referral Hook
 *
 * Manages referral/invite system including:
 * - Fetching user's invite code
 * - Checking and binding invite codes
 * - Managing referral state
 * - Fetching dashboard data with rewards
 * - Claiming rewards
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuthContext } from '@/context/AuthContext';
import * as referralApi from '@/api/referral';
import { ReferralError } from '@/api/referral';

export interface ReferralErrorInfo {
  title: string;
  message: string;
  details?: string;
}

/**
 * Get a friendly title for error codes
 */
function getErrorTitle(code: string): string {
  const titles: Record<string, string> = {
    INVALID_INVITE_CODE: 'Invalid Referral Code',
    CANNOT_INVITE_YOURSELF: 'Self-Referral Not Allowed',
    USER_ALREADY_HAS_INVITE_CODE: 'Already Bound',
    CANNOT_USE_INVITEE_INVITE_CODE: 'Invalid Referral Chain',
    AUTHENTICATION_REQUIRED: 'Authentication Required',
    NETWORK_ERROR: 'Connection Error',
    INVALID_REQUEST: 'Invalid Request',
    USER_NOT_FOUND: 'User Not Found',
  };
  return titles[code] || 'Referral Code Error';
}

/**
 * Get detailed explanation for error codes
 */
function getErrorDetails(code: string): string | undefined {
  const details: Record<string, string> = {
    INVALID_INVITE_CODE: 
      'The code you entered does not exist or may have been mistyped. Referral codes are 5 characters long and case-insensitive.',
    USER_ALREADY_HAS_INVITE_CODE: 
      'Your account is already linked to a referral network. Each account can only be bound once.',
    CANNOT_INVITE_YOURSELF: 
      'You cannot use your own referral code. Ask a friend for their code or share yours with others.',
    CANNOT_USE_INVITEE_INVITE_CODE: 
      'You cannot use a referral code from someone you have already invited. This prevents circular referrals.',
    AUTHENTICATION_REQUIRED: 
      'Please connect your wallet and complete authentication to bind a referral code.',
    NETWORK_ERROR: 
      'Unable to connect to the server. Please check your internet connection and try again.',
    USER_NOT_FOUND: 
      'Your user account could not be found. Please try signing in again.',
  };
  return details[code];
}

export function useReferral() {
  const { isAuthenticated, userInfo } = useAuthContext();
  const queryClient = useQueryClient();
  const [bindError, setBindError] = useState<ReferralErrorInfo | null>(null);

  // Fetch user's invite info
  const {
    data: inviteInfo,
    isLoading: isLoadingInfo,
    error: infoError,
  } = useQuery({
    queryKey: ['inviteInfo', userInfo?.address],
    queryFn: async () => {
      const response = await referralApi.getInviteInfo();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to fetch invite info');
      }
      return response.data;
    },
    enabled: isAuthenticated && !!userInfo?.address,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch user's dashboard data
  const {
    data: dashboard,
    isLoading: isLoadingDashboard,
    error: dashboardError,
  } = useQuery({
    queryKey: ['dashboard', userInfo?.address],
    queryFn: async () => {
      const response = await referralApi.getDashboard();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to fetch dashboard');
      }
      return response.data;
    },
    enabled: isAuthenticated && !!userInfo?.address,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check invite code validity (now only used for UI feedback, not required before binding)
  const checkCodeMutation = useMutation({
    mutationFn: async (inviteCode: string) => {
      try {
        const response = await referralApi.checkInviteCode(inviteCode);
        if (response.code !== 0) {
          throw new Error(response.message || 'Failed to check invite code');
        }
        return response.data.is_valid;
      } catch (error) {
        if (error instanceof ReferralError) {
          throw error;
        }
        throw new Error('Failed to verify invite code');
      }
    },
  });

  // Bind invite code - improved to handle all validation server-side
  const bindCodeMutation = useMutation({
    mutationFn: async (inviteCode: string) => {
      try {
        const response = await referralApi.bindInviteCode(inviteCode);
        if (response.code !== 0) {
          throw new ReferralError(
            response.error || 'UNKNOWN_ERROR',
            response.message || 'Failed to bind invite code'
          );
        }
        return response;
      } catch (error) {
        if (error instanceof ReferralError) {
          // Check for authentication errors
          if (error.statusCode === 401 || error.statusCode === 403) {
            throw new ReferralError(
              'AUTHENTICATION_REQUIRED',
              'Please authenticate with your wallet to bind an invite code'
            );
          }
          throw error;
        }
        throw new ReferralError(
          'NETWORK_ERROR',
          'Network error while binding invite code'
        );
      }
    },
    onSuccess: () => {
      // Invalidate and refetch invite info, dashboard, pre-launch status, and access check
      queryClient.invalidateQueries({ queryKey: ['inviteInfo'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['preLaunchStatus'] }); // Refresh pre-launch access
      queryClient.invalidateQueries({ queryKey: ['accessCheck'] }); // Critical: PreLaunchGuard uses this
      toast.success('Successfully bound to referral code! You are now earning extra points!');
      setBindError(null); // Clear any previous errors
    },
    onError: (error) => {
      let title = 'Referral Code Error';
      let message = 'Failed to bind invite code. Please try again.';
      let details: string | undefined;

      if (error instanceof ReferralError) {
        title = getErrorTitle(error.code);
        message = error.message; // Use the detailed message from the API client
        details = getErrorDetails(error.code);
      }

      setBindError({ title, message, details });
    },
  });

  // Claim rewards mutation
  const claimRewardsMutation = useMutation({
    mutationFn: async () => {
      const response = await referralApi.claimRewards();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to claim rewards');
      }
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate dashboard to refresh claimable amounts
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['userProgress'] });
      toast.success(
        `Successfully claimed ${data.points_claimed} points and ${data.sol_claimed} SOL!`
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to claim rewards');
    },
  });

  return {
    // State
    inviteInfo,
    isLoadingInfo,
    infoError,
    dashboard,
    isLoadingDashboard,
    dashboardError,
    hasInviter: !!inviteInfo?.invite_by,
    userInviteCode: inviteInfo?.invite_code,
    inviteCount: inviteInfo?.invite_l1_count ?? 0,

    // Error state
    bindError,
    clearBindError: () => setBindError(null),

    // Methods
    checkCode: checkCodeMutation.mutateAsync,
    bindCode: bindCodeMutation.mutate,
    claimRewards: claimRewardsMutation.mutate,
    isCheckingCode: checkCodeMutation.isPending,
    isBindingCode: bindCodeMutation.isPending,
    isClaimingRewards: claimRewardsMutation.isPending,
  };
}
